import { create } from "zustand";
import {
  createJSONStorage,
  devtools,
  persist,
  subscribeWithSelector,
} from "zustand/middleware";

import type { IVideoVisitStore } from "@components/organisms/videoVisit";
import { IVideoVisitStepEnum } from "@components/organisms/videoVisit/types";

export const useVideoVisitStore = create<IVideoVisitStore>()(
  subscribeWithSelector(
    persist(
      devtools(
        (set) => ({
          title: "videoVisitTitle",
          appointmentId: undefined,
          open: false,
          step: IVideoVisitStepEnum.LOADING,
          data: undefined,
          settingStep: "mic_setting",
          settings: {
            cam: undefined,
            speaker: undefined,
            mic: undefined,
            camLabel: undefined,
            micLabel: undefined,
            speakerLabel: undefined,
            setCam: (id, label) =>
              set((state) => ({
                settings: { ...state.settings, cam: id, camLabel: label },
                meetSettings: {
                  ...state.meetSettings,
                  cam: id,
                  camLabel: label,
                },
              })),
            setMic: (id, label) =>
              set((state) => ({
                settings: { ...state.settings, mic: id, micLabel: label },
                meetSettings: {
                  ...state.meetSettings,
                  mic: id,
                  micLabel: label,
                },
              })),
            setSpeaker: (id, label) =>
              set((state) => ({
                settings: {
                  ...state.settings,
                  speaker: id,
                  speakerLabel: label,
                },
                meetSettings: {
                  ...state.meetSettings,
                  speaker: id,
                  speakerLabel: label,
                },
              })),
          },
          meetSettings: {
            cam: undefined,
            speaker: undefined,
            mic: undefined,
            camLabel: undefined,
            micLabel: undefined,
            speakerLabel: undefined,
            setCam: (id, label) =>
              set((state) => ({
                meetSettings: {
                  ...state.meetSettings,
                  cam: id,
                  camLabel: label,
                },
              })),
            setMic: (id, label) =>
              set((state) => ({
                meetSettings: {
                  ...state.meetSettings,
                  mic: id,
                  micLabel: label,
                },
              })),
            setSpeaker: (id, label) =>
              set((state) => ({
                meetSettings: {
                  ...state.meetSettings,
                  speaker: id,
                  speakerLabel: label,
                },
              })),
          },
          livekit: {
            room: undefined,
            setRoom: (room) =>
              set((state) => ({ livekit: { ...state.livekit, room } })),
          },
          minimize: false,
          layout: {
            topbarOpen: true,
            setTopbarOpen: (value) =>
              set((state) => ({
                layout: { ...state.layout, topbarOpen: value },
              })),
          },
          sound: {
            active: true,
            volume: 100,
            setSound: (active, volume) => {
              if (!active || volume === 0) {
                set((state) => ({
                  sound: {
                    ...state.sound,
                    active: false,
                    volume: volume || state?.sound?.volume || 0,
                  },
                }));
              } else if (volume) {
                set((state) => ({
                  sound: { ...state.sound, active: true, volume },
                }));
              } else {
                set((state) => ({ sound: { ...state.sound, active: true } }));
              }
            },
          },
          setMinimize: (state) => set({ minimize: state }),
          setSettingStep: (step) => set({ settingStep: step }),
          setData: (data) => set({ data }),
          setStep: (step) => set({ step }),
          openVideoVisit: (id, role, isGuest, guestName) =>
            set(
              isGuest
                ? {
                    open: true,
                    appointmentId: id,
                    role,
                    step: IVideoVisitStepEnum.GUEST_LOADING,
                    guestName,
                  }
                : { open: true, appointmentId: id, role },
            ),
          closeVideoVisit: () => {
            return set((state) => resetStore(state));
          },
          role: "guest",
          guestName: "",
          chats: [],
          setChats: (chats) => set({ chats }),
          hasNewMessage: false,
          setHasNewMessage: (state) => set({ hasNewMessage: state }),
          sidebarActiveKey: undefined,
          setSidebarActiveKey: (state) => set({ sidebarActiveKey: state }),
          sendChat: undefined,
          setSendChat: (func) => set({ sendChat: func }),
        }),
        {
          enabled: !import.meta.env.PROD,
          name: "videoVisitStore",
        },
      ),
      {
        name: "videoVisitStore",
        partialize: () => ({
          // open: state.open,
          // appointmentId: state.appointmentId
          // step: state.step,
          // settingStep:state.settingStep,
          // TODO: Must Discuss
        }),
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
  ),
);

const resetStore = (state: IVideoVisitStore): Partial<IVideoVisitStore> => {
  state?.livekit?.room?.disconnect(true);
  return {
    livekit: { ...state.livekit, room: undefined },
    data: undefined,
    role: "guest",
    open: false,
    sound: { ...state.sound, active: true, volume: 100 },
    layout: {
      ...state.layout,
      topbarOpen: true,
    },
    settings: {
      ...state.settings,
      cam: undefined,
      speaker: undefined,
      camLabel: undefined,
      micLabel: undefined,
      mic: undefined,
    },
    step: IVideoVisitStepEnum.LOADING,
    minimize: false,
    appointmentId: undefined,
    settingStep: "mic_setting",
    chats: [],
    sendChat: undefined,
    sidebarActiveKey: undefined,
  };
};
